// =============================================================================
// preferencesCoquille.js — les réglages personnels de « Moi »
// (refonte « Mosaïque vivante », lot L6 ; synthèse §5.4 et §6.3, décision n° 10).
//
//   Thème        Jour / Nuit / Système     localStorage eniovote_theme (clé
//                                          historique ; « system » est neuf)
//   Animations   Système / Réduites        <html data-mouvement="reduit">,
//                                          lu par mouvementReduit() et les jetons
//   Transparence réduite ou non            <html data-transparence="reduite">
//                                          active reduireTransparence() de
//                                          coquilleStyles.js (classes
//                                          .enio-verre, .enio-lavis, .fen-voile,
//                                          .enio-voile ; theme.css n'y est pour
//                                          rien) : verre opaque, voiles plus
//                                          couvrants, aucun flou ;
//                                          prefers-reduced-transparency du
//                                          système fait de même (Chrome, Edge ;
//                                          Safari ne le transmet pas au web :
//                                          sur iPhone et iPad, seul « Moi »
//                                          compte) et verrouille l'interrupteur
//   Sons         désactivés PAR DÉFAUT     <html data-sons="1"> ; joués par
//                                          src/sons.js (toast de succès, pages
//                                          à jeton ; rituels du lot L8)
//   Taille       A / A+ / A++              inchangé (appShared, zoom du body)
//
// Tout est mémorisé sous try/catch (navigation privée de Safari) et posé sur
// <html> au démarrage par appliquerPreferences() (App.jsx). Chaque choisir*()
// annonce « aramis:preferences » : « Moi » et la palette se relisent, d'où
// que vienne le geste. « Complètes »
// (forcer les animations malgré le réglage du système) n'est PAS proposé : les
// règles « prefers-reduced-motion » de l'application ne savent pas l'annuler,
// l'option mentirait.
// =============================================================================
import { useEffect, useState } from "react";
import { getTheme, setThemeLS, applyTheme } from "./themeCoquille";

const CLES = { mouvement: "aramis_mouvement", transparence: "aramis_transparence", sons: "aramis_sons" };
const lire = (k) => { try { return localStorage.getItem(k) || ""; } catch { return ""; } };
const ecrire = (k, v) => { try { if (v) localStorage.setItem(k, v); else localStorage.removeItem(k); } catch { /* stockage indisponible */ } };
const poser = (attr, v) => {
  if (typeof document === "undefined") return;
  if (v) document.documentElement.setAttribute(attr, v); else document.documentElement.removeAttribute(attr);
};

export const lirePreferences = () => ({
  theme: getTheme(),                                   // "light" | "dark" | "system"
  mouvement: lire(CLES.mouvement) === "reduit" ? "reduit" : "",
  transparence: lire(CLES.transparence) === "reduite" ? "reduite" : "",
  sons: lire(CLES.sons) === "1",
});

/** Pose les réglages mémorisés sur <html> (au démarrage). */
export function appliquerPreferences() {
  const p = lirePreferences();
  poser("data-mouvement", p.mouvement);
  poser("data-transparence", p.transparence);
  poser("data-sons", p.sons ? "1" : "");
  return p;
}

const annoncer = () => { try { window.dispatchEvent(new CustomEvent("aramis:preferences")); } catch { /* vieux navigateur */ } };

export function choisirTheme(t) { setThemeLS(t); applyTheme(t); annoncer(); }
export function choisirMouvement(v) { ecrire(CLES.mouvement, v === "reduit" ? "reduit" : ""); poser("data-mouvement", v === "reduit" ? "reduit" : ""); annoncer(); }
export function choisirTransparence(reduite) { ecrire(CLES.transparence, reduite ? "reduite" : ""); poser("data-transparence", reduite ? "reduite" : ""); annoncer(); }
export function choisirSons(actifs) { ecrire(CLES.sons, actifs ? "1" : ""); poser("data-sons", actifs ? "1" : ""); annoncer(); }

/** Le système demande-t-il moins de transparence ? (prefers-reduced-transparency ;
 *  Chrome et Edge le transmettent, Safari non). coquilleStyles.js l'applique
 *  seul (même règles, sous @media) ; « Moi » montre alors l'interrupteur actif
 *  et verrouillé. */
const MQ_TRANSPARENCE = "(prefers-reduced-transparency: reduce)";
export const transparenceSysteme = () => {
  try { return typeof window !== "undefined" && !!window.matchMedia && window.matchMedia(MQ_TRANSPARENCE).matches; }
  catch { return false; }
};
/** Les sons de confirmation sont-ils voulus ? (désactivés par défaut) */
export const sonsActifs = () => lire(CLES.sons) === "1";

/** Suit le thème du système quand le réglage vaut « Système ». À installer une
 *  fois (App.jsx) ; retourne la fonction de désinstallation. */
export function suivreThemeSysteme() {
  if (typeof window === "undefined" || !window.matchMedia) return () => {};
  const mq = window.matchMedia("(prefers-color-scheme: dark)");
  const maj = () => { if (getTheme() === "system") applyTheme("system"); };
  if (mq.addEventListener) mq.addEventListener("change", maj); else if (mq.addListener) mq.addListener(maj);
  return () => { if (mq.removeEventListener) mq.removeEventListener("change", maj); else if (mq.removeListener) mq.removeListener(maj); };
}

/** Les réglages en React, pour « Moi » et la palette. */
export function usePreferences() {
  const [p, setP] = useState(lirePreferences);
  const [systeme, setSysteme] = useState(transparenceSysteme);
  useEffect(() => {
    const maj = () => setP(lirePreferences());
    window.addEventListener("aramis:preferences", maj);
    let mq = null;
    const majSysteme = () => setSysteme(transparenceSysteme());
    try { mq = window.matchMedia ? window.matchMedia(MQ_TRANSPARENCE) : null; } catch { mq = null; }
    if (mq) { if (mq.addEventListener) mq.addEventListener("change", majSysteme); else if (mq.addListener) mq.addListener(majSysteme); }
    return () => {
      window.removeEventListener("aramis:preferences", maj);
      if (mq) { if (mq.removeEventListener) mq.removeEventListener("change", majSysteme); else if (mq.removeListener) mq.removeListener(majSysteme); }
    };
  }, []);
  return {
    ...p,
    transparenceSysteme: systeme,
    setTheme: choisirTheme,
    setMouvement: choisirMouvement,
    setTransparence: choisirTransparence,
    setSons: choisirSons,
  };
}
